import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { ReactComponent as MicrophoneIcon } from "../assets/microphone.svg";

// ============================================
// UTILITY FUNCTIONS
// ============================================

const normalizeState = (s) => {
  if (!s) return "DEFAULT";
  const up = String(s).toUpperCase();
  if (up.includes("LISTEN")) return "LISTENING";
  if (up.includes("TALK") || up.includes("SPEAK")) return "TALKING";
  if (up.includes("THINK") || up.includes("PROCESS")) return "THINKING";
  if (up.includes("WAIT") || up.includes("PAUSE") || up.includes("IDLE")) return "WAITING";
  if (up.includes("READY")) return "DEFAULT";
  return "DEFAULT";
};

const useSlidingSentences = (text, isActive, intervalMs = 3000) => {
  const sentences = useMemo(() => {
    if (!text) return [];
    return text
      .split(/(?<=[.!?])\s+/)
      .map(s => s.trim())
      .filter(Boolean);
  }, [text]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (!isActive || sentences.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= sentences.length - 1) return prev;
        return prev + 1;
      });
    }, intervalMs);
    return () => clearInterval(interval);
  }, [isActive, sentences.length, intervalMs]);

  if (!text || sentences.length === 0) {
    return { displayText: "", progress: 0, sentenceCount: 0, currentSentence: 0 };
  }

  const displayText = sentences[currentIndex] || sentences[0];
  const progress = sentences.length > 1 ? (currentIndex / (sentences.length - 1)) * 100 : 100;

  return { displayText, progress, sentenceCount: sentences.length, currentSentence: currentIndex + 1 };
};

// ============================================
// ORB CANVAS
// ============================================

const SPHERE_STATES = {
  DEFAULT:   { hex: "#5377E0" },
  TALKING:   { hex: "#41C7FA" },
  LISTENING: { hex: "#F57578" },
  THINKING:  { hex: "#9D7BFD" },
  WAITING:   { hex: "#FAAB64" },
};

const hexToRgbSphere = (hex) => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
});

const PULSE = {
  speed: 0.18,
  maxRadius: 0.72,
  startAlpha: 0.45,
  spawnRate: 28,
  ringColor: "#41C7FA",
};

const SCALE_PULSE = {
  speed: 0.055,
  base: 1.0,
  idleAmp: 0.018,
  talkAmp: 0.05,
  listenAmp: 0.028,
  thinkAmp: 0.022,
  waitAmp: 0.015,
};

const THINKING = {
  breatheSpeed: 0.015,
  breatheAmt: 0.03,
  waveSpeed: 0.35,
};

const SphereOrbCanvas = ({ state, size = 160 }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef(state);
  const rafRef = useRef(null);
  const dataRef = useRef(null);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const S = size;
    const C = S / 2;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = S * dpr;
    canvas.height = S * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const max = 80;
    const basePoints = [];
    let r = 0;
    for (let a = 0; a < max; a++) {
      basePoints.push([Math.cos(r), Math.sin(r), 0]);
      r += (Math.PI * 2) / max;
    }
    for (let a = 0; a < max; a++) basePoints.push([0, basePoints[a][0], basePoints[a][1]]);
    for (let a = 0; a < max; a++) basePoints.push([basePoints[a][1], 0, basePoints[a][0]]);

    const init = hexToRgbSphere(SPHERE_STATES.DEFAULT.hex);
    dataRef.current = {
      count: 0,
      curSpeed: 0.75,
      curTrail: 0.05,
      curR: init.r,
      curG: init.g,
      curB: init.b,
      pulseRings: [],
    };

    const draw = () => {
      const d = dataRef.current;
      const currentState = stateRef.current;
      const cfg = SPHERE_STATES[currentState] || SPHERE_STATES.DEFAULT;
      const tRgb = hexToRgbSphere(cfg.hex);

      d.curR += (tRgb.r - d.curR) * 0.025;
      d.curG += (tRgb.g - d.curG) * 0.025;
      d.curB += (tRgb.b - d.curB) * 0.025;

      let speedMult = 1.0;
      if (currentState === "TALKING") {
        speedMult = 0.72;
      } else if (currentState === "THINKING") {
        speedMult = THINKING.waveSpeed + Math.sin(d.count * THINKING.breatheSpeed) * THINKING.breatheAmt;
      } else if (currentState === "WAITING") {
        speedMult = 0.4;
      }

      let amp = SCALE_PULSE.idleAmp;
      if (currentState === "TALKING") amp = SCALE_PULSE.talkAmp;
      else if (currentState === "LISTENING") amp = SCALE_PULSE.listenAmp;
      else if (currentState === "THINKING") amp = SCALE_PULSE.thinkAmp;
      else if (currentState === "WAITING") amp = SCALE_PULSE.waitAmp;

      const scalePulse = SCALE_PULSE.base + Math.sin(d.count * SCALE_PULSE.speed) * amp;

      ctx.globalCompositeOperation = "destination-out";
      ctx.filter = "blur(18px)";
      ctx.fillStyle = `rgba(0,0,0,${d.curTrail})`;
      ctx.fillRect(0, 0, S, S);
      ctx.filter = "none";
      ctx.globalCompositeOperation = "lighter";

      if (currentState === "TALKING" && d.count % PULSE.spawnRate === 0) {
        d.pulseRings.push({ radius: 0.1, alpha: PULSE.startAlpha });
      }

      const ringRgb = hexToRgbSphere(PULSE.ringColor);
      d.pulseRings = d.pulseRings.filter((ring) => ring.alpha > 0.01);
      for (const ring of d.pulseRings) {
        const rPx = ring.radius * C;
        const grad = ctx.createRadialGradient(C, C, rPx * 0.85, C, C, rPx);
        grad.addColorStop(0, `rgba(${ringRgb.r},${ringRgb.g},${ringRgb.b},0)`);
        grad.addColorStop(0.5, `rgba(${ringRgb.r},${ringRgb.g},${ringRgb.b},${ring.alpha})`);
        grad.addColorStop(1, `rgba(${ringRgb.r},${ringRgb.g},${ringRgb.b},0)`);
        ctx.beginPath();
        ctx.arc(C, C, rPx, 0, Math.PI * 2);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 3;
        ctx.stroke();
        ring.radius += PULSE.speed * 0.015;
        ring.alpha *= 0.97;
        if (ring.radius > PULSE.maxRadius) ring.alpha = 0;
      }

      let tim = d.count / 5;
      for (let e = 0; e < 3; e++) {
        tim *= 1.7;
        let s = 1 - e / 3;
        let a = (tim * d.curSpeed * speedMult) / 59;
        const yp = Math.cos(a);
        const yp2 = Math.sin(a);
        a = (tim * d.curSpeed * speedMult) / 23;
        const xp = Math.cos(a);
        const xp2 = Math.sin(a);
        const p2 = [];
        for (let i = 0; i < basePoints.length; i++) {
          const [x, y, z] = basePoints[i];
          const y1 = y * yp + z * yp2;
          const z1 = y * yp2 - z * yp;
          const x1 = x * xp + z1 * xp2;
          const zz = x * xp2 - z1 * xp;
          const z2 = Math.pow(2, zz * s);
          p2.push([x1 * z2, y1 * z2, zz]);
        }
        s *= C * 0.62 * scalePulse;
        for (let dd = 0; dd < 3; dd++) {
          for (let i = 0; i < max; i++) {
            const b = p2[dd * max + i];
            const c2 = p2[((i + 1) % max) + dd * max];
            const brightness = 0.65 + 0.35 * Math.sin((i / max) * Math.PI * 2);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${Math.round(d.curR * brightness)},${Math.round(d.curG * brightness)},${Math.round(d.curB * brightness)},0.25)`;
            ctx.lineWidth = Math.pow(6, b[2]);
            ctx.moveTo(b[0] * s + C, b[1] * s + C);
            ctx.lineTo(c2[0] * s + C, c2[1] * s + C);
            ctx.stroke();
          }
        }
      }

      d.count += 1;
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [size]);

  return <canvas ref={canvasRef} style={{ width: size, height: size, display: "block" }} />;
};

// ============================================
// BLOB PROTOTYPE
// ============================================

const BlobPrototype = ({ guideState, guideStateText, onStartGuide, isRecording = false, onStopRecording }) => {
  const state = normalizeState(guideState?.guideState);
  const message =
    (typeof guideState?.data === "string" && guideState.data.trim()) ||
    (guideStateText && guideStateText.trim()) ||
    "";

  const { displayText } = useSlidingSentences(message, state === "TALKING", 3000);
  const stateColor = SPHERE_STATES[state]?.hex || SPHERE_STATES.DEFAULT.hex;
  const stateRgb = hexToRgbSphere(stateColor);
  const glowColor = `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.6)`;
  const [showWaitingHint, setShowWaitingHint] = useState(false);

  // Recording UI replaces the message box — no separate panel
  const showRecordingUI = state === "LISTENING" && isRecording;

  useEffect(() => {
    if (state !== "WAITING") {
      setShowWaitingHint(false);
      return undefined;
    }

    const timerId = setTimeout(() => {
      setShowWaitingHint(true);
    }, 30000);

    return () => clearTimeout(timerId);
  }, [state]);

  const handleClick = () => {
    if ((state === "DEFAULT" || state === "WAITING") && onStartGuide) {
      onStartGuide();
    }
  };

  const getStateLabel = () => {
    switch (state) {
      case "TALKING":   return "Speaking";
      case "LISTENING": return "Listening";
      case "THINKING":  return "Processing";
      case "WAITING":   return "Waiting";
      case "DEFAULT":
      default:          return "Ready";
    }
  };

  const getDisplayMessage = () => {
    if (state === "TALKING" && displayText) return displayText;
    if (state === "LISTENING") return "I'm listening...";
    if (state === "THINKING") return "Let me think about that...";
    if (state === "WAITING") return message ? `${message}` : "";
    return message || "";
  };

  return (
    <Box
      onClick={!showRecordingUI ? handleClick : undefined}
      sx={{
        width: "100%",
        p: 2,
        height: 280,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        pointerEvents: "auto",
        cursor: (!showRecordingUI && (state === "DEFAULT" || state === "WAITING")) ? "pointer" : "default",
        position: "relative",
      }}
    >
      {/* Orb */}
      <Box
        sx={{
          mb: 0.75,
          height: 112,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        <SphereOrbCanvas state={state} size={105} />
      </Box>

      {/* State label */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          px: 1.25,
          py: 0.4,
          mb: 0.6,
          borderRadius: "20px",
          background: "rgba(0, 0, 0, 0.25)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#fff",
            textShadow: `0 0 10px ${glowColor}, 0 1px 2px rgba(0,0,0,0.5)`,
          }}
        >
          {getStateLabel()}
        </Typography>
      </Box>

      {/*
        The message box area — when recording it transforms into the recording UI in-place.
        No separate box, no extra height.
      */}
      {showRecordingUI ? (
        <Box
          sx={{
            width: "100%",
            maxWidth: 280,
            px: 1.5,
            py: 1.1,
            borderRadius: "12px",
            border: `1px solid rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.35)`,
            background: "rgba(0,0,0,0.38)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.9,
            animation: "recFadeIn 0.2s ease",
            "@keyframes recFadeIn": {
              "0%":   { opacity: 0 },
              "100%": { opacity: 1 },
            },
          }}
        >
          {/* Row: mic dot + "Recording" label + waveform bars */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              "@keyframes recordBar": {
                "0%, 100%": { transform: "scaleY(0.35)", opacity: 0.45 },
                "50%":      { transform: "scaleY(1)",    opacity: 1    },
              },
            }}
          >
            {/* Pulsing mic dot */}
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.9)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "@keyframes micRing": {
                  "0%":   { boxShadow: `0 0 0 0   rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.45)` },
                  "70%":  { boxShadow: `0 0 0 6px rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0)`    },
                  "100%": { boxShadow: `0 0 0 0   rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0)`    },
                },
                animation: "micRing 1.4s ease-out infinite",
              }}
            >
              <MicrophoneIcon width={12} height={12} />
            </Box>

            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                color: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.95)`,
              }}
            >
              Recording
            </Typography>

            {/* Waveform bars */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 3,
                    height: 11,
                    borderRadius: "999px",
                    background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.8)`,
                    animation: "recordBar 0.85s ease-in-out infinite",
                    animationDelay: `${i * 0.13}s`,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* I'm done button */}
          <Box
            component="button"
            onClick={(e) => { e.stopPropagation(); onStopRecording(); }}
            sx={{
              width: "100%",
              py: 0.8,
              borderRadius: "999px",
              border: `1px solid rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.45)`,
              background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.15)`,
              color: "#fff",
              fontSize: "0.82rem",
              fontWeight: 700,
              letterSpacing: "0.03em",
              cursor: "pointer",
              transition: "background 0.15s ease, border-color 0.15s ease",
              fontFamily: "inherit",
              "&:hover": {
                background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.28)`,
                borderColor: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.65)`,
              },
              "&:active": {
                transform: "scale(0.97)",
              },
            }}
          >
            I&apos;m done
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: 280,
            px: 1.5,
            py: 1,
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.2)",
            background: "rgba(0,0,0,0.38)",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.85rem",
              lineHeight: 1.45,
              color: "rgba(255,255,255,0.95)",
              textAlign: "center",
              width: "100%",
            }}
          >
            {getDisplayMessage()}
          </Typography>
        </Box>
      )}

      {!showRecordingUI && showWaitingHint && (
        <Typography
          sx={{
            mt: 0.65,
            fontSize: "0.8rem",
            color: "rgb(255, 255, 255)",
            letterSpacing: "0.01em",
            textAlign: "center",
          }}
        >
          Lost? Tap the orb to replay.
        </Typography>
      )}

      <Box sx={{ height: 18, mt: 1 }} />
    </Box>
  );
};

// ============================================
// WRAPPER
// ============================================

const GuidePrototypes = ({ guideState, guideStateText, startGuide, isRecording = false, onStopRecording }) => {
  return (
    <Box sx={{ width: "100%", display: "flex", pointerEvents: "auto" }}>
      <BlobPrototype
        guideState={guideState}
        guideStateText={guideStateText}
        onStartGuide={startGuide}
        isRecording={isRecording}
        onStopRecording={onStopRecording}
      />
    </Box>
  );
};

export default GuidePrototypes;
export { BlobPrototype };
